/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Page frame for the Real Estate screens: DashboardLayout + DashboardNavbar,
 * a title row with actions, and a snackbar hook so every screen reports
 * success and backend error_code messages the same way.
 */

import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Box, ButtonBase, Card, Icon, IconButton, Snackbar, TextField, Tooltip,
} from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import PageHeader from 'components/v2/PageHeader';
import { FilterBar as V2FilterBar, FilterSearch } from 'components/v2/filters';
import colors from 'styles/tokens/colors';
import { v2 as t, v2Density as d } from 'styles/tokens/v2';
import { errorMessage } from '../api';
import { useIsReV2 } from '../../v2/parts';
import { ReFilterBarContext } from './filterContext';

/**
 * v2 title row: the v2 PageHeader (title, one-line summary, actions) with a
 * back button before it. The page's actions go in one box so PageHeader's
 * "only the last filled button is primary" rule does not repaint the page's
 * own primary action (Issue SPA, Renew, Edit come first on these screens).
 */
export function RePageHeaderV2({ title, subtitle, actions, onBack }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      {onBack && (
        <Tooltip title="Back">
          <ButtonBase
            type="button"
            aria-label="Back"
            onClick={onBack}
            sx={{
              width: d.controlHeightPx,
              height: d.controlHeightPx,
              flexShrink: 0,
              borderRadius: `${d.radiusPx}px`,
              border: `1px solid ${t.border2}`,
              bgcolor: t.surface,
              color: t.text2,
              '&:hover': { bgcolor: t.hover, color: t.text },
              '&.Mui-focusVisible': { outline: `2px solid ${t.link}`, outlineOffset: 2 },
            }}
          >
            <ArrowLeft size={18} aria-hidden="true" />
          </ButtonBase>
        </Tooltip>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <PageHeader
          title={title}
          summary={subtitle}
          actions={actions ? (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>{actions}</Box>
          ) : null}
        />
      </Box>
    </Box>
  );
}

RePageHeaderV2.propTypes = {
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  actions: PropTypes.node,
  onBack: PropTypes.func,
};

RePageHeaderV2.defaultProps = { subtitle: null, actions: null, onBack: null };

export function RePage({ title, subtitle, actions, onBack, children }) {
  const isV2 = useIsReV2();
  if (isV2) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={2} pb={3}>
          <RePageHeaderV2 title={title} subtitle={subtitle} actions={actions} onBack={onBack} />
          {children}
        </MDBox>
      </DashboardLayout>
    );
  }
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={2} pb={3}>
        <MDBox
          display="flex"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={1.5}
          mb={2.5}
        >
          <MDBox display="flex" alignItems="center" gap={1}>
            {onBack && (
              <Tooltip title="Back">
                <IconButton size="small" onClick={onBack}>
                  <Icon>arrow_back</Icon>
                </IconButton>
              </Tooltip>
            )}
            <MDBox>
              <MDTypography variant="h5" fontWeight="bold" sx={{ color: colors.text.dark }}>
                {title}
              </MDTypography>
              {subtitle && (
                <MDTypography variant="button" sx={{ color: colors.text.light }} component="div">
                  {subtitle}
                </MDTypography>
              )}
            </MDBox>
          </MDBox>
          {actions && (
            <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center">
              {actions}
            </MDBox>
          )}
        </MDBox>
        {children}
      </MDBox>
    </DashboardLayout>
  );
}

RePage.propTypes = {
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  actions: PropTypes.node,
  onBack: PropTypes.func,
  children: PropTypes.node,
};

RePage.defaultProps = { subtitle: null, actions: null, onBack: null, children: null };

/** Flat white card with hairline border (the InfoCard grammar) without a header. */
export function RePanel({ children, sx, p }) {
  const isV2 = useIsReV2();
  if (isV2) {
    // theme-v2's card: 1px v2 border, 10px radius, no shadow.
    return (
      <Card sx={{ overflow: 'hidden', ...sx }}>
        <MDBox p={p}>{children}</MDBox>
      </Card>
    );
  }
  return (
    <Card sx={{ borderRadius: '12px', border: '1px solid #E8EDF5', boxShadow: 'none', overflow: 'hidden', ...sx }}>
      <MDBox p={p}>{children}</MDBox>
    </Card>
  );
}

RePanel.propTypes = { children: PropTypes.node, sx: PropTypes.object, p: PropTypes.number };
RePanel.defaultProps = { children: null, sx: {}, p: 2 };

/**
 * const [snack, notify] = useSnack();
 * notify('Saved'); notify.error(err, 'Could not save');
 * render {snack} once in the page.
 */
export function useSnack() {
  const [state, setState] = useState({ open: false, message: '', severity: 'success' });
  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);
  const notify = useCallback((message, severity = 'success') => setState({ open: true, message, severity }), []);
  notify.error = (err, fallback) => setState({ open: true, message: errorMessage(err, fallback), severity: 'error' });
  const element = (
    <Snackbar
      open={state.open}
      autoHideDuration={state.severity === 'error' ? 9000 : 5000}
      onClose={close}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <Alert severity={state.severity} onClose={close} variant="filled" sx={{ maxWidth: 520 }}>
        {state.message}
      </Alert>
    </Snackbar>
  );
  return [element, notify];
}

/** Inline error for a failed load. */
export function LoadError({ error, onRetry }) {
  if (!error) return null;
  return (
    <Alert
      severity="error"
      sx={{ mb: 2 }}
      action={onRetry ? (
        <IconButton size="small" onClick={onRetry} color="inherit"><Icon>refresh</Icon></IconButton>
      ) : null}
    >
      {errorMessage(error, 'Could not load data')}
    </Alert>
  );
}

LoadError.propTypes = { error: PropTypes.any, onRetry: PropTypes.func };
LoadError.defaultProps = { error: null, onRetry: null };

/** Horizontal wrap of filter controls. */
export function FilterBar({ children }) {
  const isV2 = useIsReV2();
  if (isV2) {
    // The v2 filter row; the pickers inside render the v2 filter controls.
    return (
      <ReFilterBarContext.Provider value>
        <V2FilterBar sx={{ mb: 2 }}>{children}</V2FilterBar>
      </ReFilterBarContext.Provider>
    );
  }
  return (
    <MDBox display="flex" flexWrap="wrap" gap={1.5} alignItems="center" mb={2}>
      {children}
    </MDBox>
  );
}

FilterBar.propTypes = { children: PropTypes.node };
FilterBar.defaultProps = { children: null };

/** A list's search box: v2 FilterSearch, or the classic outlined TextField. */
export function ReSearch({ label, value, onChange, sx }) {
  const isV2 = useIsReV2();
  // 240 wide so a list's whole filter row fits one line on a laptop screen.
  if (isV2) return <FilterSearch value={value} onChange={(v) => onChange(v)} placeholder={label} width={240} />;
  return <TextField size="small" label={label} value={value} onChange={(e) => onChange(e.target.value)} sx={sx} />;
}

ReSearch.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  sx: PropTypes.object,
};

ReSearch.defaultProps = { value: '', sx: undefined };

export default RePage;
